import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Card, Button, Toast, Input, Label } from '../ui';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
  subscribeToConversations,
  subscribeToUnreadCount,
  subscribeToMessagingSettings,
  updateMessagingSettings,
  getContactableUsers,
  findOrCreateConversation,
  createActivationRequest,
  subscribeToActivationRequest,
  ContactableUser
} from '../../services/messagingService';
import { activateMessagingForTesting } from '../../services/creditService';
import { Conversation, MessagingSettings, MessageChannel, MessagingActivationRequest } from '../../types';
import ConversationView from './ConversationView';
import ComposeMessage from './ComposeMessage';
import BulkMessage from './BulkMessage';
import CreditBalance from './CreditBalance';

const ALL_CHANNELS: MessageChannel[] = ['whatsapp', 'sms', 'email'];

const Messaging: React.FC = () => {
  const { selectedOrganization } = useApp();
  const { currentUser, userData } = useAuth();
  const { canWrite } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const { conversationId } = useParams<{ conversationId?: string }>();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<MessagingSettings | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(conversationId || null);
  const [showCompose, setShowCompose] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [channelFilter, setChannelFilter] = useState<MessageChannel[]>([...ALL_CHANNELS]);

  // Activation request
  const [activationRequest, setActivationRequest] = useState<MessagingActivationRequest | null>(null);
  const [requestingActivation, setRequestingActivation] = useState(false);

  // Test activation form
  const [showTestActivation, setShowTestActivation] = useState(false);
  const [testConfig, setTestConfig] = useState({
    contactPhone: '',
    contactEmail: '',
    country: '',
    address: '',
  });
  const [activating, setActivating] = useState(false);

  // Auto-reply popover
  const [showAutoReplyPopover, setShowAutoReplyPopover] = useState(false);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyMessage, setAutoReplyMessage] = useState('');
  const autoReplyRef = useRef<HTMLDivElement>(null);

  // Guardian sidebar
  const [guardians, setGuardians] = useState<ContactableUser[]>([]);
  const [guardiansLoading, setGuardiansLoading] = useState(false);
  const [guardianSearch, setGuardianSearch] = useState('');
  const [startingChat, setStartingChat] = useState<string | null>(null);
  const [guardianChannel, setGuardianChannel] = useState<MessageChannel>('whatsapp');

  // Get active tab from URL
  const activeTab = parseInt(searchParams.get('tab') || '0');

  // Load initial data and set up subscriptions
  useEffect(() => {
    if (!selectedOrganization?.id) return;

    setLoading(true);

    // Real-time settings subscription
    const unsubscribeSettings = subscribeToMessagingSettings(
      selectedOrganization.id,
      (s) => {
        setSettings(s);
        if (s) {
          setAutoReplyEnabled(s.autoReplyEnabled || false);
          setAutoReplyMessage(s.autoReplyMessage || '');
        }
        setLoading(false);
      }
    );

    // Real-time activation request subscription
    const unsubscribeActivation = subscribeToActivationRequest(
      selectedOrganization.id,
      setActivationRequest
    );

    const unsubscribeActive = subscribeToConversations(
      selectedOrganization.id,
      setConversations,
      'active'
    );

    const unsubscribeUnread = subscribeToUnreadCount(
      selectedOrganization.id,
      setTotalUnread
    );

    return () => {
      unsubscribeSettings();
      unsubscribeActivation();
      unsubscribeActive();
      unsubscribeUnread();
    };
  }, [selectedOrganization?.id]);

  useEffect(() => {
    if (conversationId) {
      setSelectedConversationId(conversationId);
    }
  }, [conversationId]);

  // Close auto-reply popover on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (autoReplyRef.current && !autoReplyRef.current.contains(e.target as Node)) {
        setShowAutoReplyPopover(false);
      }
    };
    if (showAutoReplyPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAutoReplyPopover]);

  const handleTabChange = (tab: number) => {
    setSearchParams({ tab: tab.toString() });
    setSelectedConversationId(null);
    setShowCompose(false);
  };

  const handleSelectConversation = (id: string) => {
    setSelectedConversationId(id);
    setShowCompose(false);
  };

  const handleNewMessage = () => {
    setShowCompose(true);
    setSelectedConversationId(null);
  };

  const handleConversationCreated = (id: string) => {
    setShowCompose(false);
    setSelectedConversationId(id);
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
  };

  const handleRequestActivation = async () => {
    if (!selectedOrganization?.id || !currentUser) return;
    setRequestingActivation(true);
    try {
      await createActivationRequest(
        selectedOrganization.id,
        selectedOrganization.name,
        { userId: currentUser.uid, name: userData?.name || 'Unknown' }
      );
      showToast('Activation request submitted successfully', 'success');
    } catch (error) {
      console.error('Error requesting activation:', error);
      showToast('Failed to submit activation request', 'error');
    } finally {
      setRequestingActivation(false);
    }
  };

  const handleAutoReplyToggle = async () => {
    if (!selectedOrganization?.id) return;
    const newValue = !autoReplyEnabled;
    setAutoReplyEnabled(newValue);
    try {
      await updateMessagingSettings(selectedOrganization.id, { autoReplyEnabled: newValue });
    } catch (error) {
      console.error('Error updating auto-reply:', error);
      setAutoReplyEnabled(!newValue);
    }
  };

  const handleAutoReplyMessageSave = async () => {
    if (!selectedOrganization?.id) return;
    try {
      await updateMessagingSettings(selectedOrganization.id, { autoReplyMessage: autoReplyMessage });
    } catch (error) {
      console.error('Error saving auto-reply message:', error);
    }
  };

  const tabs = [
    { label: 'Inbox', count: totalUnread },
    { label: 'Bulk Message', count: 0 }
  ];

  const [sidebarView, setSidebarView] = useState<'conversations' | 'guardians'>('conversations');

  // Load guardians when sidebar view is guardians
  useEffect(() => {
    if (sidebarView === 'guardians' && selectedOrganization?.id) {
      setGuardiansLoading(true);
      getContactableUsers(selectedOrganization.id, guardianChannel, 'guardian')
        .then(setGuardians)
        .catch(console.error)
        .finally(() => setGuardiansLoading(false));
    }
  }, [sidebarView, selectedOrganization?.id, guardianChannel]);

  const handleStartChat = async (guardian: ContactableUser) => {
    if (!selectedOrganization?.id) return;

    setStartingChat(guardian.id);
    try {
      const conversation = await findOrCreateConversation(
        selectedOrganization.id,
        guardian.id,
        guardian.name,
        guardian.type,
        guardianChannel,
        { phone: guardian.phone, email: guardian.email }
      );
      setSelectedConversationId(conversation.id);
      setSidebarView('conversations');
    } catch (error) {
      console.error('Error starting chat:', error);
      showToast('Failed to start conversation', 'error');
    } finally {
      setStartingChat(null);
    }
  };

  const filteredGuardians = guardians.filter(g => {
    const contactField = guardianChannel === 'email' ? (g.email || '') : (g.phone || '');
    return guardianSearch === '' ||
      g.name.toLowerCase().includes(guardianSearch.toLowerCase()) ||
      contactField.includes(guardianSearch);
  });

  // Filter conversations by selected channels
  const filteredConversations = channelFilter.length === ALL_CHANNELS.length
    ? conversations
    : conversations.filter(c => channelFilter.includes(c.channel));

  // Check if messaging is configured (has settings doc with any channel active)
  const isConfigured = settings && (
    settings.twilioAccountSid ||
    settings.twilioSubaccountSid ||
    settings.whatsappEnabled ||
    settings.smsEnabled ||
    settings.emailEnabled
  );

  // Not configured — show activation empty state
  if (!loading && !isConfigured) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Messages</h1>
          <p className="text-secondary-600">Communication with players and guardians</p>
        </div>

        {/* Activation Empty State */}
        <div className="flex flex-col items-center justify-center min-h-[450px]">
          <div className="w-20 h-20 rounded-full bg-secondary-100 flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>

          {!activationRequest || activationRequest.status === 'rejected' || activationRequest.status === 'approved' ? (
            <>
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">
                Messaging Not Activated
              </h2>
              <p className="text-secondary-500 mb-6 text-center max-w-md">
                {activationRequest?.status === 'rejected'
                  ? 'Your previous activation request was rejected. You can submit a new request.'
                  : activationRequest?.status === 'approved'
                  ? 'A previous activation did not complete successfully. Please activate again.'
                  : 'Request messaging activation to start communicating with players and guardians via WhatsApp, SMS, and Email.'}
              </p>

              {activationRequest?.status === 'rejected' && activationRequest.rejectionReason && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl max-w-md w-full">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-red-800">Rejection Reason</p>
                      <p className="text-sm text-red-700 mt-1">{activationRequest.rejectionReason}</p>
                    </div>
                  </div>
                </div>
              )}

              {canWrite('messaging') && (
                <div className="flex flex-col items-center gap-3">
                  <Button
                    onClick={handleRequestActivation}
                    disabled={requestingActivation}
                  >
                    {requestingActivation ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {activationRequest?.status === 'rejected' ? 'Request Again' : 'Request Messaging Activation'}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTestActivation(true)}
                  >
                    Activate for Testing
                  </Button>
                </div>
              )}
            </>
          ) : activationRequest.status === 'pending' ? (
            <>
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">
                Activation Request Pending
              </h2>
              <p className="text-secondary-500 mb-6 text-center max-w-md">
                Your messaging activation request has been submitted and is being reviewed by the platform administrator. You'll be notified once it's approved.
              </p>
              <div className="flex items-center gap-2 px-4 py-2.5 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="animate-pulse w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                <span className="text-sm font-medium text-yellow-800">Pending Review</span>
              </div>
            </>
          ) : null}
        </div>

        {/* Test Activation Form */}
        {showTestActivation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-secondary-900">Activate Messaging for Testing</h3>
                  <button onClick={() => setShowTestActivation(false)} className="text-secondary-400 hover:text-secondary-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <p className="text-sm text-secondary-500">
                  Provide your organization's contact information to activate messaging. A messaging account will be created automatically and you'll receive 10 test credits.
                </p>

                <div className="space-y-3">
                  <div>
                    <Label>Contact Phone *</Label>
                    <Input
                      placeholder="+1234567890"
                      value={testConfig.contactPhone}
                      onChange={(e) => setTestConfig(prev => ({ ...prev, contactPhone: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Contact Email *</Label>
                    <Input
                      type="email"
                      placeholder="contact@yourorg.com"
                      value={testConfig.contactEmail}
                      onChange={(e) => setTestConfig(prev => ({ ...prev, contactEmail: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input
                      placeholder="e.g. United States"
                      value={testConfig.country}
                      onChange={(e) => setTestConfig(prev => ({ ...prev, country: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input
                      placeholder="Organization address"
                      value={testConfig.address}
                      onChange={(e) => setTestConfig(prev => ({ ...prev, address: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowTestActivation(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    disabled={activating || !testConfig.contactPhone || !testConfig.contactEmail}
                    onClick={async () => {
                      if (!selectedOrganization?.id) return;
                      setActivating(true);
                      try {
                        await activateMessagingForTesting(
                          selectedOrganization.id,
                          selectedOrganization.name,
                          testConfig.contactPhone,
                          testConfig.contactEmail,
                          testConfig.country || undefined,
                          testConfig.address || undefined
                        );
                        setShowTestActivation(false);
                        showToast('Messaging activated! You have 10 test credits.', 'success');
                      } catch (err: any) {
                        console.error('Test activation failed:', err);
                        const message = err?.message?.includes('already-exists')
                          ? 'Messaging is already activated for this organization'
                          : 'Failed to activate messaging. Please try again.';
                        showToast(message, 'error');
                      } finally {
                        setActivating(false);
                      }
                    }}
                  >
                    {activating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Activating...
                      </>
                    ) : 'Activate'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Messages</h1>
          <p className="text-secondary-600">Communication with players and guardians</p>
        </div>
        <div className="flex items-center gap-3">
          <CreditBalance compact showToast={showToast} />

          {/* Auto-reply gear icon */}
          {canWrite('messaging') && isConfigured && (
            <div className="relative" ref={autoReplyRef}>
              <button
                onClick={() => setShowAutoReplyPopover(!showAutoReplyPopover)}
                className={`p-2 rounded-lg transition-colors ${
                  showAutoReplyPopover
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                }`}
                title="Auto-reply settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>

              {/* Auto-reply popover */}
              {showAutoReplyPopover && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-secondary-200 rounded-xl shadow-lg z-50 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-secondary-900">Auto-Reply</h4>
                    <button
                      onClick={handleAutoReplyToggle}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        autoReplyEnabled ? 'bg-primary-600' : 'bg-secondary-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          autoReplyEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-xs text-secondary-500">
                    Automatically reply to incoming messages when enabled.
                  </p>
                  <textarea
                    value={autoReplyMessage}
                    onChange={(e) => setAutoReplyMessage(e.target.value)}
                    onBlur={handleAutoReplyMessageSave}
                    placeholder="Enter auto-reply message..."
                    rows={3}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    disabled={!autoReplyEnabled}
                  />
                </div>
              )}
            </div>
          )}

          {canWrite('messaging') && activeTab === 0 && isConfigured && (
            <Button onClick={handleNewMessage}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Message
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab, index) => (
            <button
              key={tab.label}
              onClick={() => handleTabChange(index)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2
                ${activeTab === index
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }
              `}
            >
              {tab.label}
              {tab.count > 0 && index === 0 && (
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-100 text-primary-700">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="flex justify-center items-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {/* Inbox Tab */}
          {activeTab === 0 && (
            <div className="flex gap-6">
              {/* Sidebar */}
              <div className="w-72 flex-shrink-0">
                <Card className="h-full">
                  {/* Toggle: Chats / Guardians */}
                  <div className="p-2 border-b border-secondary-200">
                    <div className="flex bg-secondary-100 rounded-lg p-1">
                      <button
                        onClick={() => setSidebarView('conversations')}
                        className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          sidebarView === 'conversations'
                            ? 'bg-white text-secondary-900 shadow-sm'
                            : 'text-secondary-600 hover:text-secondary-900'
                        }`}
                      >
                        Chats
                      </button>
                      <button
                        onClick={() => setSidebarView('guardians')}
                        className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          sidebarView === 'guardians'
                            ? 'bg-white text-secondary-900 shadow-sm'
                            : 'text-secondary-600 hover:text-secondary-900'
                        }`}
                      >
                        Guardians
                      </button>
                    </div>
                  </div>

                  {sidebarView === 'conversations' ? (
                    <>
                      {/* Channel filter chips */}
                      <div className="px-3 pt-3 pb-1 flex flex-wrap gap-1.5">
                        <button
                          onClick={() => setChannelFilter([...ALL_CHANNELS])}
                          className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                            channelFilter.length === ALL_CHANNELS.length
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                          }`}
                        >
                          All
                        </button>
                        {([
                          { value: 'whatsapp' as MessageChannel, label: 'WhatsApp' },
                          { value: 'sms' as MessageChannel, label: 'SMS' },
                          { value: 'email' as MessageChannel, label: 'Email' }
                        ]).map(ch => (
                          <button
                            key={ch.value}
                            onClick={() => {
                              if (channelFilter.includes(ch.value)) {
                                if (channelFilter.length > 1) {
                                  setChannelFilter(channelFilter.filter(c => c !== ch.value));
                                }
                              } else {
                                setChannelFilter([...channelFilter, ch.value]);
                              }
                            }}
                            className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                              channelFilter.includes(ch.value)
                                ? 'bg-primary-100 text-primary-700'
                                : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                            }`}
                          >
                            {ch.label}
                          </button>
                        ))}
                      </div>
                      {/* Search */}
                      <div className="p-3 border-b border-secondary-200">
                        <div className="relative">
                          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <Input
                            type="text"
                            placeholder="Search chats..."
                            className="pl-10 text-sm"
                          />
                        </div>
                      </div>
                      {/* Conversation List */}
                      <div className="overflow-y-auto max-h-[calc(100vh-350px)]">
                        {filteredConversations.length === 0 ? (
                          <div className="p-6 text-center">
                            <p className="text-secondary-500 text-sm">No conversations yet</p>
                          </div>
                        ) : (
                          filteredConversations.map(conversation => {
                            const channelIconSmall = conversation.channel === 'sms'
                              ? <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                              : conversation.channel === 'email'
                              ? <svg className="w-3 h-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                              : <svg className="w-3 h-3 text-emerald-600" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>;

                            return (
                              <div
                                key={conversation.id}
                                onClick={() => handleSelectConversation(conversation.id)}
                                className={`p-3 border-b border-secondary-100 cursor-pointer transition-colors ${
                                  selectedConversationId === conversation.id
                                    ? 'bg-primary-50 border-l-4 border-l-primary-500'
                                    : 'hover:bg-secondary-50'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                                    conversation.participantType === 'player'
                                      ? 'bg-blue-100 text-blue-700'
                                      : conversation.participantType === 'guardian'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {conversation.participantName.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-1 min-w-0">
                                        <p className="font-medium text-secondary-900 text-sm truncate">
                                          {conversation.participantType === 'unknown'
                                            ? conversation.participantPhone
                                            : conversation.participantName}
                                        </p>
                                        {channelIconSmall}
                                      </div>
                                      {conversation.unreadCount > 0 && (
                                        <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-primary-500 text-white">
                                          {conversation.unreadCount}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-secondary-500 truncate">
                                      {conversation.lastMessagePreview || 'No messages'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Guardian channel picker */}
                      <div className="px-3 pt-3 pb-1 flex gap-1.5">
                        {(['whatsapp', 'sms', 'email'] as MessageChannel[]).map(ch => (
                          <button
                            key={ch}
                            onClick={() => setGuardianChannel(ch)}
                            className={`px-2.5 py-1 text-xs font-medium rounded-full capitalize transition-colors ${
                              guardianChannel === ch
                                ? 'bg-primary-100 text-primary-700'
                                : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                            }`}
                          >
                            {ch}
                          </button>
                        ))}
                      </div>
                      {/* Guardian Search */}
                      <div className="p-3 border-b border-secondary-200">
                        <div className="relative">
                          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <Input
                            type="text"
                            placeholder="Search guardians..."
                            value={guardianSearch}
                            onChange={(e) => setGuardianSearch(e.target.value)}
                            className="pl-10 text-sm"
                          />
                        </div>
                      </div>
                      {/* Guardian List */}
                      <div className="overflow-y-auto max-h-[calc(100vh-350px)]">
                        {guardiansLoading ? (
                          <div className="p-6 text-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                            <p className="text-secondary-500 text-sm mt-2">Loading...</p>
                          </div>
                        ) : filteredGuardians.length === 0 ? (
                          <div className="p-6 text-center">
                            <p className="text-secondary-500 text-sm">
                              {guardianSearch ? 'No guardians found' : 'No guardians available'}
                            </p>
                          </div>
                        ) : (
                          filteredGuardians.map((guardian) => (
                            <div
                              key={guardian.id}
                              onClick={() => handleStartChat(guardian)}
                              className="p-3 border-b border-secondary-100 cursor-pointer hover:bg-secondary-50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-semibold">
                                  {guardian.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-secondary-900 text-sm truncate">{guardian.name}</p>
                                  <p className="text-xs text-secondary-500">
                                    {guardianChannel === 'email' ? guardian.email : guardian.phone}
                                  </p>
                                </div>
                                {startingChat === guardian.id && (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </Card>
              </div>

              {/* Conversation View / Compose */}
              <div className="flex-1">
                {showCompose ? (
                  <ComposeMessage
                    onConversationCreated={handleConversationCreated}
                    onCancel={() => setShowCompose(false)}
                  />
                ) : selectedConversationId ? (
                  <ConversationView
                    conversationId={selectedConversationId}
                    onBack={() => setSelectedConversationId(null)}
                  />
                ) : (
                  <Card className="p-8 text-center h-full flex flex-col items-center justify-center min-h-[400px]">
                    <svg className="w-16 h-16 text-secondary-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h3 className="text-lg font-medium text-secondary-900 mb-2">
                      Select a conversation
                    </h3>
                    <p className="text-secondary-500">
                      Choose a conversation from the list or start a new one.
                    </p>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Bulk Message Tab */}
          {activeTab === 1 && (
            <BulkMessage showToast={showToast} />
          )}
        </>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Messaging;
